'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { ColumnDefWithSkeleton } from '@morpho-org/ui-v6/types';
import type { Address } from 'viem';
import { CursorPagination } from '@morpho-org/ui-v6/client';
import { TableNoResults } from '@morpho-org/ui-v6/server';
import { cn } from '@morpho-org/ui-v6/utils';
import { rowSortingFeature, tableFeatures, useTable } from '@tanstack/react-table';
import { ErrorDataUnavailable } from '@/components/common/Error';
import { SubSectionTitle } from '@/components/common/SubSectionTitle';
import { Table, TrBodyRow, TrHeaderGroup } from '@/components/common/Table';
import { TableScrollArea } from '@/components/common/Table/CompactTable/Table';
import { VAULT_TABLE_5_ROWS_HEIGHT } from '@/components/VaultPage/config';
import { useSkeletonRows } from '@/hooks/useSkeletonRows';
import { PAGE_SIZE, type VaultDistributionTableRow } from '../config';
import { useDistributionAsset } from '../hooks/useDistributionAsset';
import { useVaultHoldersData } from '../hooks/useVaultHoldersData';
import { useVaultHoldersRows } from '../hooks/useVaultHoldersRows';
import { VaultDistributionPageErrorNotice } from './VaultDistributionPageErrorNotice';
import { VaultDistributionPartialNotice } from './VaultDistributionPartialNotice';

// Keeps enableSorting: false effective; without rowSortingFeature, accessor headers render as sortable.
export const vaultDistributionTableFeatures = tableFeatures({
  rowSortingFeature
});
export type VaultDistributionTableFeatures = typeof vaultDistributionTableFeatures;
export interface VaultDistributionTableProps {
  address: Address;
  chainId: ChainId;
  vaultVersion: 'v1' | 'v2';
  columns: ColumnDefWithSkeleton<VaultDistributionTableFeatures, VaultDistributionTableRow>[];
  scrollAreaClassName?: string;
}
interface DistributionViewInput {
  rowCount: number;
  page: {
    partial: boolean;
  } | undefined;
  error: Error | null;
  nextPageFailed: boolean;
  loading: boolean;
  assetUnavailable: boolean;
}

/**
 * Which parts of the table show. Only a failed first load replaces the table; a failed page change
 * keeps the current page and its pagination so the user can retry or go back. A partial page with
 * no rows is unavailable rather than empty.
 */
const toDistributionView = ({
  rowCount,
  page,
  error,
  nextPageFailed,
  loading,
  assetUnavailable
}: DistributionViewInput) => {
  const hasRows = rowCount > 0;
  const partial = page?.partial ?? false;
  const firstLoadFailed = error != null && page === undefined;
  const pageChangeFailed = nextPageFailed && page !== undefined && !loading;
  const showError = !loading && !hasRows && (firstLoadFailed || partial || assetUnavailable);
  return {
    hasRows,
    partial,
    firstLoadFailed,
    pageChangeFailed,
    showError,
    showEmpty: !loading && !hasRows && !showError,
    showTable: loading || hasRows
  };
};

/** The holders of a vault, largest first, five per page behind Previous / Next. */
export const VaultDistributionTable = t0 => {
  const $ = _c(66);
  const {
    address,
    chainId,
    vaultVersion,
    columns,
    scrollAreaClassName
  } = t0;
  let t1;
  if ($[0] !== address || $[1] !== chainId || $[2] !== vaultVersion) {
    t1 = {
      chainId,
      address,
      vaultVersion,
      limit: PAGE_SIZE
    };
    $[0] = address;
    $[1] = chainId;
    $[2] = vaultVersion;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    page,
    items,
    isLoading,
    isLoadingNextPage,
    error,
    nextPageFailed,
    pageIndex,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    prefetchNext
  } = useVaultHoldersData(t1);
  let t2;
  if ($[4] !== address || $[5] !== chainId || $[6] !== vaultVersion) {
    t2 = {
      chainId,
      address,
      vaultVersion
    };
    $[4] = address;
    $[5] = chainId;
    $[6] = vaultVersion;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const assetQuery = useDistributionAsset(t2);
  let t3;
  if ($[8] !== assetQuery.data || $[9] !== chainId || $[10] !== items || $[11] !== pageIndex) {
    t3 = {
      items,
      asset: assetQuery.data,
      chainId,
      pageIndex
    };
    $[8] = assetQuery.data;
    $[9] = chainId;
    $[10] = items;
    $[11] = pageIndex;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const rows = useVaultHoldersRows(t3);
  let t4;
  if ($[13] !== columns || $[14] !== rows) {
    t4 = {
      features: vaultDistributionTableFeatures,
      data: rows,
      columns,
      enableSorting: false
    };
    $[13] = columns;
    $[14] = rows;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const table = useTable(t4);
  const loading = isLoading || isLoadingNextPage || assetQuery.isPending;
  const assetUnavailable = !assetQuery.isPending && !assetQuery.data;
  let t5;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      pageIndex: 0,
      pageSize: PAGE_SIZE
    };
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== columns) {
    t6 = {
      first: PAGE_SIZE,
      totalCount: PAGE_SIZE,
      pagination: t5,
      columnsDefs: columns
    };
    $[17] = columns;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const skeletonRows = useSkeletonRows(t6);
  let t7;
  if ($[19] !== assetUnavailable || $[20] !== error || $[21] !== loading || $[22] !== nextPageFailed || $[23] !== page || $[24] !== rows.length) {
    t7 = toDistributionView({
      rowCount: rows.length,
      page,
      error,
      nextPageFailed,
      loading,
      assetUnavailable
    });
    $[19] = assetUnavailable;
    $[20] = error;
    $[21] = loading;
    $[22] = nextPageFailed;
    $[23] = page;
    $[24] = rows.length;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const {
    hasRows,
    partial,
    firstLoadFailed,
    pageChangeFailed,
    showError,
    showEmpty,
    showTable
  } = t7;
  let t8;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <SubSectionTitle>User Distribution</SubSectionTitle>;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== scrollAreaClassName) {
    t9 = cn("grid h-full w-full", scrollAreaClassName);
    $[27] = scrollAreaClassName;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== loading || $[30] !== showTable || $[31] !== skeletonRows || $[32] !== table) {
    t10 = showTable && <Table.Table tableVariant="compact"><Table.Thead>{table.getHeaderGroups().map(_temp)}</Table.Thead><Table.Tbody>{!loading && table.getRowModel().rows.map(_temp2)}{loading && skeletonRows.map(_temp3)}</Table.Tbody></Table.Table>;
    $[29] = loading;
    $[30] = showTable;
    $[31] = skeletonRows;
    $[32] = table;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== showEmpty) {
    t11 = showEmpty && <TableNoResults text="No depositor found." height={VAULT_TABLE_5_ROWS_HEIGHT} style={{
      borderRadius: "0"
    }} />;
    $[34] = showEmpty;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== showError) {
    t12 = showError && <ErrorDataUnavailable minHeight={VAULT_TABLE_5_ROWS_HEIGHT} style={{
      borderRadius: "0"
    }} />;
    $[36] = showError;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== t10 || $[39] !== t11 || $[40] !== t12 || $[41] !== t9) {
    t13 = <TableScrollArea className={t9}>{t10}{t11}{t12}</TableScrollArea>;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t9;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== hasRows || $[44] !== loading || $[45] !== partial) {
    t14 = partial && hasRows && !loading && <VaultDistributionPartialNotice />;
    $[43] = hasRows;
    $[44] = loading;
    $[45] = partial;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== assetUnavailable || $[48] !== pageChangeFailed) {
    t15 = pageChangeFailed && !assetUnavailable && <VaultDistributionPageErrorNotice />;
    $[47] = assetUnavailable;
    $[48] = pageChangeFailed;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== assetUnavailable || $[51] !== canNextPage || $[52] !== canPreviousPage || $[53] !== error || $[54] !== firstLoadFailed || $[55] !== loading || $[56] !== nextPage || $[57] !== pageIndex || $[58] !== prefetchNext || $[59] !== previousPage) {
    t16 = !assetUnavailable && <CursorPagination loading={loading} error={firstLoadFailed ? error : null} pageIndex={pageIndex} previousPage={previousPage} nextPage={nextPage} canPreviousPage={canPreviousPage} canNextPage={canNextPage} onPrefetchNext={prefetchNext} />;
    $[50] = assetUnavailable;
    $[51] = canNextPage;
    $[52] = canPreviousPage;
    $[53] = error;
    $[54] = firstLoadFailed;
    $[55] = loading;
    $[56] = nextPage;
    $[57] = pageIndex;
    $[58] = prefetchNext;
    $[59] = previousPage;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  let t17;
  if ($[61] !== t13 || $[62] !== t14 || $[63] !== t15 || $[64] !== t16) {
    t17 = <div className="gap-sm flex flex-col" data-testid="vault-distribution">{t8}<div className="gap-xs flex flex-col">{t13}{t14}{t15}{t16}</div></div>;
    $[61] = t13;
    $[62] = t14;
    $[63] = t15;
    $[64] = t16;
    $[65] = t17;
  } else {
    t17 = $[65];
  }
  return t17;
};
function _temp(headerGroup) {
  return <TrHeaderGroup key={headerGroup.id} headerGroup={headerGroup} />;
}
function _temp2(row) {
  return <TrBodyRow key={row.id} row={row} />;
}
function _temp3(row_0, index) {
  return <TrBodyRow key={`skeleton-table-distribution-${index}`} skeletonRow={row_0} />;
}